<template>
  <div class="mapbox-track">
    <div v-if="!hasToken" class="alert alert-warning token-warn">
      Mapbox token ausente. Defina <code>VUE_APP_MAPBOX_TOKEN</code> no ambiente
      do build para visualizar o mapa.
    </div>

    <div v-else>
      <div ref="map" class="map-canvas"></div>

      <div class="map-controls">
        <label>
          <input type="checkbox" v-model="satellite" @change="toggleSatellite" />
          Satélite
        </label>
        <label>
          <input type="checkbox" v-model="show3d" @change="toggleTerrain" />
          Relevo 3D
        </label>
        <span class="legend">
          <span class="swatch ref"></span> Rota oficial
          <span class="swatch athlete"></span> Trace do atleta
          <span v-if="kmMarkers && kmMarkers.length" class="lbl-key time">horário</span>
          <span v-if="paceMarkers && paceMarkers.length" class="lbl-key pace">ritmo/km</span>
          <span v-if="alertSegments && alertSegments.length" class="lbl-key alert">⚠ ritmo suspeito</span>
          <span v-if="gapSegments && gapSegments.length" class="lbl-key gap">sem registro</span>
        </span>
      </div>

      <!-- Inline elevation profile (no chart lib): SVG from the athlete track -->
      <div v-if="elevationPath" class="elevation">
        <svg :viewBox="`0 0 ${elevW} ${elevH}`" preserveAspectRatio="none" class="elevation-svg">
          <path :d="elevationAreaPath" class="elev-area" />
          <path :d="elevationPath" class="elev-line" />
        </svg>
        <div class="elevation-meta">
          <span>Ganho: {{ elevationGain }} m</span>
          <span>Min {{ elevMin }} m / Max {{ elevMax }} m</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'mapbox-track',

  props: {
    // GeoJSON LineString of the official route ([lon,lat,ele])
    referenceGeo: { type: Object, default: null },
    // Single athlete trace (back-compat for the per-file view)
    athleteGeo: { type: Object, default: null },
    // Multiple traces for a team: [{ geo, color, label }]
    traces: { type: Array, default: null },
    // Per-km split markers: [{ km, lon, lat, label }]
    kmMarkers: { type: Array, default: null },
    // Pace labels between km marks: [{ lon, lat, label, suspect }]
    paceMarkers: { type: Array, default: null },
    // Trechos com ritmo impossível: [{ coords: [[lon,lat],...], label }]
    alertSegments: { type: Array, default: null },
    // Buracos sem registro (km pulados): [{ coords, label }]
    gapSegments: { type: Array, default: null },
    // GeoJSON Point of start / finish
    startPoint: { type: Object, default: null },
    finishPoint: { type: Object, default: null },
    // Horário de passagem na largada/chegada (ex.: "04:05h") — exibido no
    // mapa junto aos pinos, no mesmo estilo dos rótulos de km.
    startLabel: { type: String, default: null },
    finishLabel: { type: String, default: null },
  },

  data() {
    return {
      map: null,
      show3d: false,
      satellite: true,
      markersAdded: false,
      elevW: 600,
      elevH: 90,
    };
  },

  computed: {
    hasToken() {
      return !!process.env.VUE_APP_MAPBOX_TOKEN;
    },

    // --- elevation profile from the athlete track Z coords -------------
    athleteCoords() {
      const g = this.athleteGeo;
      if (!g || g.type !== 'LineString' || !Array.isArray(g.coordinates)) return [];
      return g.coordinates;
    },
    elevations() {
      return this.athleteCoords
        .map((c) => (c.length > 2 ? Number(c[2]) : null))
        .filter((v) => v != null && isFinite(v));
    },
    elevMin() {
      return this.elevations.length ? Math.round(Math.min(...this.elevations)) : 0;
    },
    elevMax() {
      return this.elevations.length ? Math.round(Math.max(...this.elevations)) : 0;
    },
    elevationGain() {
      let gain = 0;
      let last = null;
      for (const e of this.elevations) {
        if (last != null && e - last > 1) gain += e - last;
        last = e;
      }
      return Math.round(gain);
    },
    elevationPath() {
      const els = this.elevations;
      if (els.length < 2) return null;
      const min = this.elevMin;
      const max = this.elevMax;
      const range = max - min || 1;
      const n = els.length;
      const pts = els.map((e, i) => {
        const x = (i / (n - 1)) * this.elevW;
        const y = this.elevH - ((e - min) / range) * (this.elevH - 6) - 3;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      });
      return 'M' + pts.join(' L');
    },
    elevationAreaPath() {
      if (!this.elevationPath) return null;
      return `${this.elevationPath} L${this.elevW},${this.elevH} L0,${this.elevH} Z`;
    },
  },

  mounted() {
    if (this.hasToken) this.initMap();
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  methods: {
    initMap() {
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;

      const center = this.computeCenter();
      this.map = new mapboxgl.Map({
        container: this.$refs.map,
        style: this.baseStyle(),
        center,
        zoom: 11,
      });

      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

      this.map.on('load', () => {
        this.addLayers();        // geojson sources/layers (route, traces, km)
        this.addMarkers();       // start/finish DOM markers (persist across styles)
        this.fitToData();
      });
    },

    baseStyle() {
      return this.satellite
        ? 'mapbox://styles/mapbox/satellite-streets-v12'
        : 'mapbox://styles/mapbox/outdoors-v12';
    },

    toggleSatellite() {
      if (!this.map) return;
      // setStyle wipes custom sources/layers — re-add them once the new style
      // finishes loading. DOM markers (start/finish) survive automatically.
      this.map.setStyle(this.baseStyle());
      this.map.once('style.load', () => {
        this.addLayers();
        if (this.show3d) this.applyTerrain(true);
      });
    },

    computeCenter() {
      // Prefer the athlete first point, else reference, else São Paulo.
      const pick = (g) =>
        g && g.coordinates && g.coordinates.length ? g.coordinates[0] : null;
      const c = pick(this.athleteGeo) || pick(this.referenceGeo);
      if (c) return [c[0], c[1]];
      return [-46.63, -23.55];
    },

    addLayers() {
      // Reference route (official) — drawn first, underneath.
      if (this.referenceGeo) {
        this.map.addSource('reference', {
          type: 'geojson',
          data: { type: 'Feature', geometry: this.referenceGeo, properties: {} },
        });
        this.map.addLayer({
          id: 'reference-line',
          type: 'line',
          source: 'reference',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#2c7fb8', 'line-width': 5, 'line-opacity': 0.6 },
        });
      }

      // Multiple team traces (each a different colour) — drawn on top.
      const list = (this.traces && this.traces.length)
        ? this.traces
        : (this.athleteGeo ? [{ geo: this.athleteGeo, color: '#e6550d' }] : []);
      list.forEach((tr, i) => {
        if (!tr.geo) return;
        const id = 'athlete-' + i;
        this.map.addSource(id, {
          type: 'geojson',
          data: { type: 'Feature', geometry: tr.geo, properties: {} },
        });
        this.map.addLayer({
          id: id + '-line',
          type: 'line',
          source: id,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': tr.color || '#e6550d', 'line-width': 3 },
        });
      });

      // Per-km split markers (admin analysis). Dots always visible; labels
      // shown only for 5-km marks when zoomed out, all marks when zoomed in.
      if (this.kmMarkers && this.kmMarkers.length) {
        this.map.addSource('kmsplits', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.kmMarkers.map((m) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [m.lon, m.lat] },
              // `major` precomputed because Mapbox expressions have no modulo.
              properties: {
                km: m.km,
                major: (m.km % 5 === 0),
                label: m.label || ('km ' + m.km),
                labelMajor: 'km ' + m.km + (m.label ? '\n' + m.label : ''),
              },
            })),
          },
        });
        this.map.addLayer({
          id: 'kmsplits-dot',
          type: 'circle',
          source: 'kmsplits',
          paint: {
            'circle-radius': ['case', ['get', 'major'], 5, 3],
            'circle-color': ['case', ['get', 'major'], '#c0392b', '#222'],
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 1.5,
          },
        });
        this.map.addLayer({
          id: 'kmsplits-label',
          type: 'symbol',
          source: 'kmsplits',
          layout: {
            // Mapbox requires zoom-driven expressions at the top level via
            // step/interpolate (not nested in case). Zoomed out: short label
            // (clock); zoomed in (>=13): "km N + clock".
            'text-field': ['step', ['zoom'], ['get', 'label'], 13, ['get', 'labelMajor']],
            'text-size': 15,
            'text-offset': [0, -1.0],
            'text-anchor': 'bottom',
            'text-allow-overlap': false,
          },
          paint: {
            // Yellow with a dark halo so it pops on both street and satellite.
            'text-color': '#ffe000',
            'text-halo-color': '#000',
            'text-halo-width': 2,
            // Zoomed out: only 5-km marks; zoomed in (>=13): all.
            'text-opacity': ['step', ['zoom'],
              ['case', ['get', 'major'], 1, 0],
              13, 1],
          },
        });
      }

      // Pace labels at the midpoint between km marks (a different colour).
      // Normais: só com zoom (densos). SUSPEITOS: sempre visíveis, em vermelho.
      if (this.paceMarkers && this.paceMarkers.length) {
        this.map.addSource('pacesplits', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.paceMarkers.map((m) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [m.lon, m.lat] },
              properties: { label: m.label, suspect: !!m.suspect },
            })),
          },
        });
        this.map.addLayer({
          id: 'pacesplits-label',
          type: 'symbol',
          source: 'pacesplits',
          filter: ['!', ['get', 'suspect']],
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 13,
            'text-offset': [0, 0.9],
            'text-anchor': 'top',
            'text-allow-overlap': false,
          },
          paint: {
            // Cyan/teal pace, distinct from the yellow km/time labels.
            'text-color': '#19e0ff',
            'text-halo-color': '#003244',
            'text-halo-width': 2,
            // Pace is fine detail — only when zoomed in (>=14).
            'text-opacity': ['step', ['zoom'], 0, 14, 1],
          },
        });
        this.map.addLayer({
          id: 'pacesplits-suspect',
          type: 'symbol',
          source: 'pacesplits',
          filter: ['get', 'suspect'],
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 15,
            'text-offset': [0, 0.9],
            'text-anchor': 'top',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#fff',
            'text-halo-color': '#d50000',
            'text-halo-width': 2.5,
          },
        });
      }

      // Buracos sem registro (km pulados) — tracejado laranja, sempre visível.
      if (this.gapSegments && this.gapSegments.length) {
        this.map.addSource('gaps', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.gapSegments.map((s) => ({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: s.coords },
              properties: { label: s.label || 'sem registro' },
            })),
          },
        });
        this.map.addLayer({
          id: 'gaps-line',
          type: 'line',
          source: 'gaps',
          layout: { 'line-cap': 'round' },
          paint: { 'line-color': '#ff9100', 'line-width': 5, 'line-dasharray': [1.2, 1.4] },
        });
        this.map.addLayer({
          id: 'gaps-label',
          type: 'symbol',
          source: 'gaps',
          layout: {
            'symbol-placement': 'line-center',
            'text-field': ['get', 'label'],
            'text-size': 14,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': '#fff', 'text-halo-color': '#b35600', 'text-halo-width': 2.5 },
        });
      }

      // Horário de largada/chegada junto aos pinos — mesmo estilo dos km.
      // Em circuito (largada = chegada, caso da Volta do Lago) os dois pontos
      // coincidem: junta num rótulo só para não sobrepor.
      const endpoints = [];
      const sp = this.startPoint && this.startPoint.coordinates && this.startLabel ? this.startPoint.coordinates : null;
      const fp = this.finishPoint && this.finishPoint.coordinates && this.finishLabel ? this.finishPoint.coordinates : null;
      const closeBy = sp && fp
        && Math.abs(sp[0] - fp[0]) < 0.002 && Math.abs(sp[1] - fp[1]) < 0.002; // ~200m
      if (closeBy) {
        endpoints.push({ coords: sp, label: 'Largada ' + this.startLabel + ' · Chegada ' + this.finishLabel });
      } else {
        if (sp) endpoints.push({ coords: sp, label: 'Largada ' + this.startLabel });
        if (fp) endpoints.push({ coords: fp, label: 'Chegada ' + this.finishLabel });
      }
      if (endpoints.length) {
        this.map.addSource('endpoints', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: endpoints.map((e) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [e.coords[0], e.coords[1]] },
              properties: { label: e.label },
            })),
          },
        });
        this.map.addLayer({
          id: 'endpoints-label',
          type: 'symbol',
          source: 'endpoints',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 15,
            // Acima do pino DOM (que tem ~40px de altura).
            'text-offset': [0, -2.4],
            'text-anchor': 'bottom',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#ffe000',
            'text-halo-color': '#000',
            'text-halo-width': 2,
          },
        });
      }

      // Trechos com ritmo impossível — vermelho grosso, gritante em qualquer zoom.
      if (this.alertSegments && this.alertSegments.length) {
        this.map.addSource('alerts', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.alertSegments.map((s) => ({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: s.coords },
              properties: { label: s.label || '⚠ ritmo suspeito' },
            })),
          },
        });
        this.map.addLayer({
          id: 'alerts-casing',
          type: 'line',
          source: 'alerts',
          layout: { 'line-cap': 'round' },
          paint: { 'line-color': '#fff', 'line-width': 11, 'line-opacity': 0.85 },
        });
        this.map.addLayer({
          id: 'alerts-line',
          type: 'line',
          source: 'alerts',
          layout: { 'line-cap': 'round' },
          paint: { 'line-color': '#ff1744', 'line-width': 7 },
        });
        this.map.addLayer({
          id: 'alerts-label',
          type: 'symbol',
          source: 'alerts',
          layout: {
            'symbol-placement': 'line-center',
            'text-field': ['get', 'label'],
            'text-size': 14,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': '#fff', 'text-halo-color': '#c40000', 'text-halo-width': 2.5 },
        });
      }
    },

    // Foca o mapa numa trace específica (perna/trecho) — usado quando o admin
    // abre o arquivo de um trecho.
    focusTrace(geo) {
      if (!this.map || !geo || !geo.coordinates || !geo.coordinates.length) return;
      const cs = geo.type === 'LineString' ? geo.coordinates : geo.coordinates.flat();
      const bounds = cs.reduce(
        (b, c) => b.extend([c[0], c[1]]),
        new mapboxgl.LngLatBounds([cs[0][0], cs[0][1]], [cs[0][0], cs[0][1]])
      );
      this.map.fitBounds(bounds, { padding: 70, duration: 600 });
    },

    // Start/finish DOM markers — added once (they survive setStyle).
    addMarkers() {
      if (this.markersAdded) return;
      if (this.startPoint && this.startPoint.coordinates) {
        new mapboxgl.Marker({ color: '#2ca25f' })
          .setLngLat(this.startPoint.coordinates)
          .setPopup(new mapboxgl.Popup().setText('Largada'))
          .addTo(this.map);
      }
      if (this.finishPoint && this.finishPoint.coordinates) {
        new mapboxgl.Marker({ color: '#de2d26' })
          .setLngLat(this.finishPoint.coordinates)
          .setPopup(new mapboxgl.Popup().setText('Chegada'))
          .addTo(this.map);
      }
      this.markersAdded = true;
    },

    fitToData() {
      const coordsList = [];
      const collect = (g) => {
        if (g && g.coordinates) coordsList.push(...g.coordinates);
      };
      // Visão de arquivo único (um trecho): foca na trace do atleta, não na
      // rota inteira. Sem trace, cai para rota + traces do time.
      collect(this.athleteGeo);
      if (!coordsList.length) {
        collect(this.referenceGeo);
        if (this.traces) this.traces.forEach((t) => collect(t.geo));
      }
      if (!coordsList.length) return;
      const bounds = coordsList.reduce(
        (b, c) => b.extend([c[0], c[1]]),
        new mapboxgl.LngLatBounds(
          [coordsList[0][0], coordsList[0][1]],
          [coordsList[0][0], coordsList[0][1]]
        )
      );
      this.map.fitBounds(bounds, { padding: 40, duration: 0 });
    },

    toggleTerrain() {
      this.applyTerrain(this.show3d);
    },

    applyTerrain(on) {
      if (!this.map) return;
      if (on) {
        if (!this.map.getSource('mapbox-dem')) {
          this.map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        this.map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
        this.map.easeTo({ pitch: 60, duration: 600 });
      } else {
        this.map.setTerrain(null);
        this.map.easeTo({ pitch: 0, duration: 600 });
      }
    },
  },
};
</script>

<style scoped lang="stylus">
.mapbox-track
  width: 100%

.map-canvas
  width: 100%
  height: 460px
  border-radius: 8px
  overflow: hidden

.token-warn
  padding: 1em
  background: #fff3cd
  color: #664d03
  border-radius: 6px

.map-controls
  display: flex
  align-items: center
  gap: 1.4em
  margin: 0.6em 0
  font-size: 0.9em

.legend
  display: inline-flex
  align-items: center
  gap: 0.5em
  color: #555

.swatch
  display: inline-block
  width: 18px
  height: 4px
  border-radius: 2px
  margin-left: 0.6em

.swatch.ref
  background: #2c7fb8

.swatch.athlete
  background: #e6550d

.lbl-key
  margin-left: 0.6em
  padding: 0.05em 0.4em
  border-radius: 3px
  font-size: 0.85em
  font-weight: 700

.lbl-key.time
  background: #000
  color: #ffe000

.lbl-key.pace
  background: #003244
  color: #19e0ff

.lbl-key.alert
  background: #ff1744
  color: #fff

.lbl-key.gap
  background: #ff9100
  color: #fff

.elevation
  margin-top: 0.4em

.elevation-svg
  width: 100%
  height: 90px
  background: rgba(0,0,0,0.03)
  border-radius: 6px
  display: block

.elev-area
  fill: rgba(230, 85, 13, 0.12)
  stroke: none

.elev-line
  fill: none
  stroke: #e6550d
  stroke-width: 1.5
  vector-effect: non-scaling-stroke

.elevation-meta
  display: flex
  justify-content: space-between
  font-size: 0.8em
  color: #777
  margin-top: 0.2em
</style>
